import React, { useMemo } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import type { CellClickedEvent, GridOptions } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { useTheme } from '@material-ui/core/styles';

import { AgGridTableWrapper } from '@mummssoftware/common-ui';
import type {
  AgentGateway,
  PatientConversationSummary,
} from '@mummssoftware/utils';

import { useCostColumnsShown } from '../../hooks/useShowCost';
import type { ConversationQuestion } from '../../types';
import defaults from '../../translations/en.json';
import { ConversationDetail } from './ConversationDetail';
import {
  getHistoryColumnDefs,
  HISTORY_CENTER_CLASS,
  HISTORY_NUMERIC_CLASS,
} from './historyColumnDefs';
import {
  QUESTION_DATE_FILTER_STACKED_HEIGHT,
  QuestionDateFilterContext,
  QuestionDateFilterState,
  questionDateFilterGridCss,
  withQuestionDateFilter,
} from './QuestionDateFilter';
import { LAST_TOKEN_COLUMN } from './tokenColumnDefs';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/agGridAlpineFont.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

if (LicenseManager) {
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);
}

const GridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  flex-grow: 1;
  background-color: ${(props) => props.theme.palette.background.paper};

  .ag-cell.${HISTORY_NUMERIC_CLASS} {
    font-variant-numeric: tabular-nums;
  }
  .ag-header-cell.${HISTORY_CENTER_CLASS} .ag-header-cell-menu-button {
    display: none !important;
  }
  .ag-cell.${HISTORY_CENTER_CLASS} {
    justify-content: center;
  }
  ${questionDateFilterGridCss}
  /* the questions start at the row's left edge, under its columns */
  .ag-details-row {
    padding: 0;
  }
  /* a row opens its questions and answers below it */
  .ag-row:not(.ag-full-width-row) {
    cursor: pointer;
  }
`;

const ErrorText = styled.p`
  margin: 0;
  padding: 6px 10px;
  font-size: 0.8rem;
  color: ${(props) => props.theme.palette.error?.main || '#f44336'};
`;

/** The chevron toggles the row itself; a click on it must not toggle it back. */
const isChevronClick = (event: CellClickedEvent) => {
  const target = event.event?.target;
  return (
    target instanceof Element &&
    Boolean(target.closest('.ag-group-contracted, .ag-group-expanded'))
  );
};

type HistoryGridProps = {
  agentGateway: AgentGateway;
  /** null while the first load is in flight (shows the loading overlay). */
  rows: PatientConversationSummary[] | null;
  error?: string | null;
  /** The document icon: a whole conversation, or one question of an opened row. */
  onOpenPast: (
    row: PatientConversationSummary,
    question?: ConversationQuestion,
  ) => void;
  smallWidget?: boolean;
  siteTimezone?: string | null;
  /** Expanded: the date filter, framed over Messages to the last token column. */
  dateFilter?: QuestionDateFilterState | null;
};

/**
 * Every user's MAIA conversations about this patient, newest first. A click
 * on a row opens a grid of its questions below it (master/detail); the
 * document icons open the conversation, or one question, full size. Same grid
 * stack as Incidents / Chart Audit (AgGridTableWrapper + hummingbird theme),
 * so the empty state is the dashboard's usual "No Rows To Show".
 */
export const HistoryGrid = ({
  agentGateway,
  rows,
  error,
  onOpenPast,
  smallWidget,
  siteTimezone,
  dateFilter,
}: HistoryGridProps) => {
  const {
    props: { grid: gridTheme },
  } = useTheme() as any;

  const showCost = useCostColumnsShown();
  // only whether there is a filter: its dates reach the header through context
  const hasDateFilter = Boolean(dateFilter);
  const columnDefs = useMemo(() => {
    const defs = getHistoryColumnDefs({
      onOpenPast,
      smallWidget,
      siteTimezone,
      showCost,
    });
    return hasDateFilter
      ? withQuestionDateFilter(defs, 'messages', LAST_TOKEN_COLUMN)
      : defs;
  }, [onOpenPast, smallWidget, siteTimezone, showCost, hasDateFilter]);

  const gridOptions: GridOptions = useMemo(
    () => ({
      defaultColDef: {
        resizable: true,
        sortable: true,
        suppressMenu: true,
      },
      suppressCellSelection: true,
      getRowNodeId: (data: PatientConversationSummary) => String(data.id),
      // a reload (after a chat) updates rows in place by id, so opened rows
      // stay open; their questions are drawn again, which reloads them
      immutableData: true,
      // a stored conversation's questions never change: a row closed and
      // opened again shows the ones it loaded (AG Grid keeps the last 10)
      keepDetailRows: true,
      onGridReady: (params) => params.api.sizeColumnsToFit(),
      onGridSizeChanged: (params) => params.api.sizeColumnsToFit(),
      // "See tokens" adds or removes columns: fit them again (the question
      // rows follow through columnResized)
      onDisplayedColumnsChanged: (params) => params.api.sizeColumnsToFit(),
      masterDetail: true,
      // the questions scroll sideways with the columns they line up under
      embedFullWidthRows: true,
      isRowMaster: () => true,
      detailRowAutoHeight: true,
      detailCellRenderer: 'conversationDetail',
      detailCellRendererParams: {
        agentGateway,
        onOpenQuestion: onOpenPast,
        smallWidget,
        siteTimezone,
      },
      frameworkComponents: { conversationDetail: ConversationDetail },
      onCellClicked: (event: CellClickedEvent) => {
        if (event.column.getColId() !== 'view' && !isChevronClick(event)) {
          event.node.setExpanded(!event.node.expanded);
        }
      },
    }),
    [agentGateway, onOpenPast, smallWidget, siteTimezone],
  );

  return (
    <GridWrapper data-testid="maia-history-grid">
      {error ? <ErrorText role="alert">{error}</ErrorText> : null}
      <QuestionDateFilterContext.Provider
        value={dateFilter ? { filter: dateFilter, stacked: true } : null}
      >
        <AgGridTableWrapper
          className={`${gridTheme.className} light`}
          name="maia-history-table-wrapper"
        >
          <AgGridReact
            gridOptions={gridOptions}
            columnDefs={columnDefs}
            rowData={rows ?? undefined}
            headerHeight={gridTheme?.headerHeight || 27}
            groupHeaderHeight={
              hasDateFilter ? QUESTION_DATE_FILTER_STACKED_HEIGHT : undefined
            }
            rowHeight={gridTheme?.rowHeight || 27}
            overlayNoRowsTemplate={`<span>${defaults.noConversations}</span>`}
            overlayLoadingTemplate={`<span>${defaults.loadingConversations}</span>`}
          />
        </AgGridTableWrapper>
      </QuestionDateFilterContext.Provider>
    </GridWrapper>
  );
};

export default HistoryGrid;
